import { useState, useEffect, useCallback } from 'react'
import type { StationPriceHistory, TimeRange, FuelType } from '@fuelfinder/shared'
import { fetchHistory } from '../lib/api'

interface UseStationHistoryReturn {
  history: StationPriceHistory | null
  isLoading: boolean
  error: string | null
  timeRange: TimeRange
  setTimeRange: (range: TimeRange) => void
}

function filterByTimeRange(
  dates: string[],
  prices: StationPriceHistory['prices'],
  range: TimeRange
): StationPriceHistory {
  if (range === 'max' || dates.length === 0) {
    return { dates, prices }
  }

  const now = new Date()
  let cutoffDays: number
  switch (range) {
    case '30d':
      cutoffDays = 30
      break
    case '3m':
      cutoffDays = 90
      break
    case '1y':
      cutoffDays = 365
      break
  }

  const cutoffDate = new Date(now.getTime() - cutoffDays * 24 * 60 * 60 * 1000)
  const cutoffStr = cutoffDate.toISOString().slice(0, 10)

  const filteredIndices: number[] = []
  for (let i = 0; i < dates.length; i++) {
    if (dates[i] >= cutoffStr) {
      filteredIndices.push(i)
    }
  }

  const filteredDates = filteredIndices.map((i) => dates[i])
  const filteredPrices: StationPriceHistory['prices'] = {}

  for (const ft of ['E10', 'B7', 'E5', 'SDV'] as FuelType[]) {
    const arr = prices[ft]
    if (arr) {
      filteredPrices[ft] = filteredIndices.map((i) => arr[i] ?? null)
    }
  }

  return { dates: filteredDates, prices: filteredPrices }
}

export function useStationHistory(
  stationId: string | null
): UseStationHistoryReturn {
  const [history, setHistory] = useState<StationPriceHistory | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [timeRange, setTimeRange] = useState<TimeRange>('30d')

  useEffect(() => {
    if (!stationId) {
      setHistory(null)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    fetchHistory()
      .then((data) => {
        if (cancelled) return

        const stationData = data.stations[stationId]
        if (!stationData || data.dates.length === 0) {
          setHistory({ dates: [], prices: {} })
        } else {
          setHistory(
            filterByTimeRange(data.dates, stationData, timeRange)
          )
        }
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load history')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [stationId, timeRange])

  const handleSetTimeRange = useCallback((range: TimeRange) => {
    setTimeRange(range)
  }, [])

  return {
    history,
    isLoading,
    error,
    timeRange,
    setTimeRange: handleSetTimeRange,
  }
}
